import type { SiteManifest } from './types';

/**
 * AATOZZ Health and Wellness site-content manifest. Defaults are the approved
 * design handoff (`client design handoffs/design_handoff_aatozz/`), with the
 * lines that described a different business (a café roasting its own beans,
 * a Baguio address) corrected to what the client's own Facebook page says:
 * healthy coffee, choco drinks, juice and wellness products, from San Carlos
 * City, aatozz809@gmail.com.
 *
 * Group ids are `az-*` because the manifest lint forbids any field path that
 * another shipped manifest already claims (AGP owns `home.hero.*`).
 *
 * NOT here (already runtime-editable elsewhere): the products and membership
 * kits (the live catalog + Landing Packages), the member stories (Storefront →
 * Pages → About — Company → Testimonials), the social links (storefront
 * `social`), brand names (`brand`), the header logo (Layout & Theme), nav
 * labels/routes and colors/fonts.
 */
export const AATOZZ_SITE: SiteManifest = {
  tenant: 'aatozz',
  label: 'AATOZZ Website',
  // The home page is bespoke, but /storefront is the shared marketplace, so the
  // Home tab and Featured Packages still render there, and the landing's
  // Membership section reads Landing Packages. Only the Official Store never does.
  adminHide: {
    storefrontTabs: ['official'],
  },
  groups: [
    {
      id: 'az-home',
      label: 'Home',
      sections: [
        {
          id: 'hero',
          label: 'Hero',
          fields: [
            {
              id: 'pill',
              kind: 'text',
              label: 'Label above the heading',
              default: 'Healthy coffee · Ethical network',
            },
            {
              id: 'title',
              kind: 'textarea',
              label: 'Heading',
              hint: 'Each line break starts a new line.',
              default: 'Roasted with Care,\nShared with Trust',
            },
            {
              id: 'body',
              kind: 'textarea',
              label: 'Text',
              default:
                'AATOZZ brings healthy coffee, premium choco drinks, refreshing juices and wellness products to families across the Philippines, shared by a community of members who earn by sharing what they drink.',
            },
            { id: 'primaryCta', kind: 'text', label: 'Main button', default: 'Become a Member' },
            {
              id: 'secondaryCta',
              kind: 'text',
              label: 'Second button',
              default: 'Shop Our Coffee',
            },
            {
              id: 'image',
              kind: 'image',
              label: 'Background photo',
              hint: 'Wide photo, at least 1600 px across. It sits under a dark brown tint.',
              defaultUrl: '/tenants/aatozz/hero.webp',
            },
            {
              id: 'credit',
              kind: 'text',
              label: 'Photo credit',
              hint: 'Clear this when you upload your own photo.',
              default: 'Photo by Marsela Christina on Unsplash',
            },
          ],
        },
        {
          id: 'welcome',
          label: 'Welcome',
          fields: [
            { id: 'title', kind: 'text', label: 'Heading', default: 'Welcome to AATOZZ' },
            {
              id: 'paras',
              kind: 'richParas',
              label: 'Text',
              default: [
                'Masarap at healthy: AATOZZ offers drinks made for all ages, from healthy coffee and premium choco to refreshing juice, plus a growing range of health and wellness products.',
                'Our mission is simple: great coffee, honestly priced, with a compensation plan clear enough to read over a single cup.',
              ],
            },
            {
              id: 'cards',
              kind: 'objectList',
              label: 'Cards',
              itemFields: [
                { id: 'title', kind: 'text', label: 'Title' },
                { id: 'text', kind: 'textarea', label: 'Text' },
              ],
              default: [
                {
                  title: 'Healthy Coffee & Drinks',
                  text: 'Coffee, choco and juice made to enjoy every day, for the whole family.',
                },
                {
                  title: 'A Brewing Community',
                  text: 'Members who share the drinks they actually enjoy, backed by training and real support.',
                },
                {
                  title: 'Steady, Honest Earnings',
                  text: 'A clear compensation plan with every peso traceable in your member wallet.',
                },
              ],
              fixedLength: true,
            },
          ],
        },
        {
          id: 'products',
          label: 'Our Coffee',
          hint: 'The cards are your first three products (featured first). Edit them under Products.',
          fields: [
            { id: 'title', kind: 'text', label: 'Heading', default: 'Our Coffee' },
            {
              id: 'subtitle',
              kind: 'text',
              label: 'Subheading',
              default: 'Healthy coffee, choco and juice for the whole family',
            },
            { id: 'viewAll', kind: 'text', label: 'Button', default: 'View All Products →' },
          ],
        },
        {
          id: 'packages',
          label: 'Membership Packages',
          hint: 'The cards are the kits picked under Landing Packages. A kit marked Featured is shown as the most popular; the lines of its description become the ticked list.',
          fields: [
            { id: 'title', kind: 'text', label: 'Heading', default: 'Membership Packages' },
            {
              id: 'subtitle',
              kind: 'text',
              label: 'Subheading',
              default: 'Choose the kit that fits how much coffee you want to share',
            },
            { id: 'cta', kind: 'text', label: 'Button', default: 'Get Started' },
            { id: 'popular', kind: 'text', label: 'Featured badge', default: 'Most Popular' },
          ],
        },
        {
          id: 'values',
          label: 'What We Stand For',
          fields: [
            { id: 'title', kind: 'text', label: 'Heading', default: 'What We Stand For' },
            {
              id: 'subtitle',
              kind: 'text',
              label: 'Subheading',
              default: 'The principles behind every cup and every payout',
            },
            {
              id: 'items',
              kind: 'objectList',
              label: 'Values',
              itemFields: [
                { id: 'name', kind: 'text', label: 'Name' },
                { id: 'text', kind: 'textarea', label: 'Text' },
              ],
              default: [
                {
                  name: 'Integrity',
                  text: 'Clear prices and clear commissions. Nothing hidden in the fine print.',
                },
                {
                  name: 'Craft',
                  text: 'Drinks made to taste good and feel good, cup after cup.',
                },
                {
                  name: 'Community',
                  text: 'We grow with our members, their families and the neighborhood around us.',
                },
                {
                  name: 'Wellness',
                  text: 'Healthy choices for all ages, made part of the everyday.',
                },
              ],
              fixedLength: true,
            },
          ],
        },
        {
          id: 'steps',
          label: 'How It Works',
          fields: [
            { id: 'title', kind: 'text', label: 'Heading', default: 'How It Works' },
            {
              id: 'subtitle',
              kind: 'text',
              label: 'Subheading',
              default: 'From your first cup to your first payout in three steps',
            },
            {
              id: 'items',
              kind: 'objectList',
              label: 'Steps',
              itemFields: [
                { id: 'name', kind: 'text', label: 'Name' },
                { id: 'text', kind: 'textarea', label: 'Text' },
              ],
              default: [
                {
                  name: 'Join AATOZZ',
                  text: 'Pick a membership kit, activate your slot and get your first products.',
                },
                {
                  name: 'Share the Coffee',
                  text: 'Invite friends to buy and brew. Your network grows as the coffee does.',
                },
                {
                  name: 'Earn & Grow',
                  text: 'Earn on referrals and team volume, with every payout visible in your wallet.',
                },
              ],
              fixedLength: true,
            },
          ],
        },
        {
          id: 'stories',
          label: 'Member Stories',
          hint: 'Hidden until there is at least one testimonial. Add them under Storefront → Pages → About — Company → Testimonials.',
          fields: [
            { id: 'title', kind: 'text', label: 'Heading', default: 'Member Stories' },
            {
              id: 'subtitle',
              kind: 'text',
              label: 'Subheading',
              default: 'What our customers and members say',
            },
          ],
        },
        {
          id: 'cta',
          label: 'Closing banner',
          fields: [
            { id: 'title', kind: 'text', label: 'Heading', default: 'Ready to Brew With Us?' },
            {
              id: 'body',
              kind: 'textarea',
              label: 'Text',
              default:
                'Pick a kit, pour a cup, and start sharing coffee you can stand behind. Every peso you earn shows up in your wallet, plainly labeled.',
            },
            {
              id: 'primaryCta',
              kind: 'text',
              label: 'Main button',
              default: 'See Membership Kits',
            },
            { id: 'secondaryCta', kind: 'text', label: 'Second button', default: 'Visit Us' },
          ],
        },
      ],
    },
    {
      id: 'az-contact',
      label: 'Contact & Footer',
      sections: [
        {
          id: 'details',
          label: 'Contact details',
          hint: 'Shown in the footer. Leave a line empty to hide it.',
          fields: [
            {
              id: 'address',
              kind: 'text',
              label: 'Address',
              default: 'San Carlos City, Philippines',
            },
            { id: 'hours', kind: 'text', label: 'Opening hours', default: '' },
            { id: 'phone', kind: 'text', label: 'Phone', default: '' },
            { id: 'email', kind: 'text', label: 'Email', default: 'aatozz809@gmail.com' },
          ],
        },
        {
          id: 'footer',
          label: 'Footer',
          fields: [
            {
              id: 'logo',
              kind: 'image',
              label: 'Logo',
              hint: 'Shown on a cream plate. A transparent PNG works best.',
              defaultUrl: '/tenants/aatozz/logo-lockup.png',
            },
            {
              id: 'tagline',
              kind: 'text',
              label: 'Tagline',
              default: 'Roasted with care. Shared with trust.',
            },
            {
              id: 'blurb',
              kind: 'textarea',
              label: 'Short description',
              default: 'Healthy coffee, drinks and wellness products for every Filipino family.',
            },
            { id: 'contactTitle', kind: 'text', label: 'Contact heading', default: 'Visit Us' },
            {
              id: 'copyright',
              kind: 'text',
              label: 'Copyright line',
              hint: '"© <year>" is added in front.',
              default: 'AATOZZ. All rights reserved.',
            },
          ],
        },
      ],
    },
  ],
};
